import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Handles BOTH sign-in callbacks:
 * - Magic-link:  /auth/callback?token=...        (existing)
 * - Google:      /auth/callback#session_id=...   (Emergent OAuth fragment)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export const AuthCallback = () => {
    const [params] = useSearchParams();
    const { verifyMagicLink, exchangeGoogleSession, linkProfile } = useAuth();
    const { profileId } = useProfile();
    const navigate = useNavigate();
    const [state, setState] = useState({ status: "verifying", error: null, user: null });
    // useRef guard so React StrictMode's double-mount doesn't burn the one-shot token twice
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;

        const queryToken = params.get("token");
        const hash = window.location.hash || "";
        const sessionMatch = hash.match(/session_id=([^&]+)/);
        const googleSessionId = sessionMatch ? decodeURIComponent(sessionMatch[1]) : null;

        if (!queryToken && !googleSessionId) {
            setState({ status: "error", error: "Missing sign-in token." });
            return;
        }

        (async () => {
            try {
                let data;
                if (googleSessionId) {
                    data = await exchangeGoogleSession(googleSessionId);
                    // Clear the fragment from the URL so back-button refresh doesn't replay it
                    window.history.replaceState(null, "", window.location.pathname);
                    toast.success("Signed in with Google", { description: data.user.email });
                } else {
                    data = await verifyMagicLink(queryToken);
                    toast.success("Signed in", { description: data.user.email });
                }

                // If we already have a local profile, auto-link it to the user.
                if (profileId && !data.user.profile_id) {
                    try { await linkProfile(profileId); } catch { /* non-fatal */ }
                }

                setState({ status: "ok", user: data.user });
                // Determine post-auth destination
                let nextPath = data.next;
                if (!nextPath) {
                    try { nextPath = sessionStorage.getItem("fitsme.post_auth_next"); } catch (e) { /* private mode */ }
                }
                try { sessionStorage.removeItem("fitsme.post_auth_next"); } catch (e) { /* ignore */ }
                setTimeout(() => navigate(nextPath || "/profile", { replace: true }), 800);
            } catch (err) {
                const msg = err?.response?.data?.detail || err.message || "Sign-in failed";
                setState({ status: "error", error: msg });
            }
        })();
    }, [params, verifyMagicLink, exchangeGoogleSession, navigate, profileId, linkProfile]);

    return (
        <div className="max-w-md mx-auto py-24 px-6">
            <div className="border-4 border-black bg-white shadow-brutal-lg p-6 text-center">
                {state.status === "verifying" && (
                    <div data-testid="auth-callback-verifying">
                        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" />
                        <p className="font-display text-2xl font-black uppercase">Signing you in…</p>
                    </div>
                )}
                {state.status === "ok" && (
                    <div data-testid="auth-callback-success">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-3" />
                        <p className="font-display text-2xl font-black uppercase">Welcome</p>
                        <p className="font-mono text-xs mt-2">{state.user?.email}</p>
                        <p className="font-mono text-[10px] uppercase tracking-widest mt-4 text-black/60">/ Redirecting /</p>
                    </div>
                )}
                {state.status === "error" && (
                    <div data-testid="auth-callback-error">
                        <XCircle className="w-12 h-12 mx-auto mb-3" />
                        <p className="font-display text-2xl font-black uppercase">Couldn't sign in</p>
                        <p className="font-mono text-xs mt-2 text-black/70">{state.error}</p>
                        <Link to="/login" data-testid="auth-callback-retry" className="inline-block mt-6 border-2 border-black bg-[#CCFF00] px-4 py-2 font-mono text-xs uppercase tracking-widest shadow-brutal-sm">
                            Try again →
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};
